import { useState } from 'react'
import { useIsSentinelUser } from '../../../authUserProvider'
import { useFundableClients } from './useFundableClients'
import s from './styles.module.scss'

interface Props {
    introducer: string
    title?: string
    // Optional PCP Referral campaign cohort ('current' | 'historical'); scopes
    // the fundable count to that window. Omit for all-time.
    cohort?: string
}

const REASON_LABELS: Record<string, string> = {
    ccj_or_va: 'Excluded: CCJ / Voluntary Arrangement',
    missing_cfa: 'Excluded: No CFA on file',
    no_valid_claims: 'Excluded: No claims with policy number',
    no_eligible_claims: 'Excluded: No eligible claims',
    below_minimum_client_value: 'Excluded: Client value below batch minimum',
    missing_policy_number: 'Claim level: Missing policy number',
    dedupe_policy_number: 'Claim level: Policy number matched dedupe table',
    no_loan_value: 'Claim level: No loan value',
    below_minimum_loan: 'Claim level: Loan below £5,000',
    invalid_start_date: 'Claim level: Invalid start date',
    future_start_date: 'Claim level: Start date in future',
    pre_2007_start_date: 'Claim level: Pre-2007 start date',
    missing_lender: 'Claim level: Missing lender',
    unapproved_lender: 'Claim level: Lender not on approved list',
    missing_length_of_contract: 'Claim level: Missing contract length',
    below_minimum_contract_length: 'Claim level: Contract length under 5 months',
    below_minimum_monthly_repayment: 'Claim level: Monthly repayment < £50',
    excessive_monthly_repayment: 'Claim level: Monthly repayment > £1,500',
}

const formatNumber = (n: number) =>
    typeof n === 'number' && !isNaN(n) ? new Intl.NumberFormat('en-GB').format(n) : '0'

// Feature flag — temporarily disabled across all introducer overview pages
// while the underlying `leadsWithFullClientDataForEligibility` query is being
// fixed (PCP Referral schema is missing `address_line_one`/`address_line_two`,
// and the pcpReferral selector has a latent `JSON.parse("undefined")` crash
// when a query errors). Flip back to `true` once both are resolved.
// @see utilities/mysql/pcpReferral/sqlQueries/leads.js
// @see utilities/mysql/pcpReferral/select.js
const FEATURE_ENABLED = true

const FundableClientsStatImpl = ({ introducer, title = 'Fundable Clients (Lifetime)', cohort }: Props) => {
    const [showBreakdown, setShowBreakdown] = useState(false)
    const { data, loading, error, reload } = useFundableClients(introducer, true, cohort)

    const breakdownEntries = data ? Object.entries(data.byReason).sort((a, b) => b[1] - a[1]) : []

    return (
        <div className={s.wrapper}>
            <div className={s.card}>
                <div className={s.accentBar} />
                <div className={s.header}>
                    <div>
                        <div className={s.title}>{title}</div>
                        <div className={s.subtitle}>
                            Clients eligible to enter a funding batch — runs the full fundability predicate, so this can
                            take a moment.
                        </div>
                    </div>
                    {data && !loading && !error && breakdownEntries.length > 0 && (
                        <button type="button" className={s.toggle} onClick={() => setShowBreakdown((v) => !v)}>
                            {showBreakdown ? 'Hide exclusion breakdown' : 'Why are clients excluded?'}
                        </button>
                    )}
                </div>

                {loading && (
                    <div className={s.loading}>
                        <span className={s.spinner} />
                        Calculating…
                    </div>
                )}

                {!loading && error && (
                    <div className={s.error}>
                        <span>{error}</span>
                        <button type="button" className={s.retry} onClick={reload}>
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !error && data && (
                    <>
                        <div className={s.valueRow}>
                            <div className={s.value}>{formatNumber(data.fundableClients)}</div>
                            <div className={s.outOf}>of {formatNumber(data.totalClients)} clients evaluated</div>
                        </div>
                        {showBreakdown && (
                            <div className={s.breakdown}>
                                {breakdownEntries.map(([reason, count]) => (
                                    <div key={reason} className={s.breakdownRow}>
                                        <span className={s.breakdownLabel}>{REASON_LABELS[reason] || reason}</span>
                                        <span className={s.breakdownValue}>{formatNumber(count)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export const FundableClientsStat = (props: Props) => {
    const isSentinelUser = useIsSentinelUser()
    if (!FEATURE_ENABLED) return null
    // The detailed card (headline number + internal exclusion-reason breakdown)
    // is admin-only. Regular introducer users instead get the plain fundable
    // number swapped into their "Clients with Claims" stat tile inside StatsView
    // (see the `fundableIntroducer` prop), so this card is hidden for them.
    if (!isSentinelUser) return null
    return <FundableClientsStatImpl {...props} />
}

export default FundableClientsStat
